// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { RefObject } from 'react';

import type { Resource, ResourceKey, SurveyPart, SurveyPartPatch, SurveyType } from '../../../api/types';
import { useArmedConfirm } from '../../../app/useArmedConfirm';
import { isScanPart } from '../../../bygning/provenance';
import { roomName } from '../../../kortlaegning/model';
import { Button } from '../../Button';
import { Pill } from '../../Pill';
import { TextLink } from '../../TextLink';
import styles from './Inspector.module.css';
import { PartFields } from './PartFields';

export interface PartFocusProps {
  type: SurveyType;
  part: SurveyPart;
  resource: Resource | null;
  catalogue: readonly ResourceKey[];
  canEdit: boolean;
  busy: boolean;
  home: RefObject<HTMLElement | null>;
  onPatch: (patch: SurveyPartPatch) => void;
  onCellCommit: (keyId: string, value: string | null, label: string) => Promise<void>;
  onInvalid: (label: string) => void;
  /** After the armed confirm: `DELETE /resources/{code}` (a scan part is tombstoned). */
  onDelete: () => void;
  onClose: () => void;
}

/**
 * The focused part (`?part=`) under the Dele chips in Gennemgang's type
 * inspector (Ressourcer opens a part in its own `PartInspector` instead): its
 * quantity, note and part-scoped values (`PartFields`, folded under "Delens
 * egenskaber"), its ★, and Slet behind an armed confirm.
 */
export function PartFocus(props: PartFocusProps) {
  const { type, part, resource, catalogue, canEdit, busy, home, onPatch, onCellCommit, onInvalid, onDelete, onClose } = props;
  const confirm = useArmedConfirm<string>(busy, part.code);
  const armed = confirm.armed === part.code;
  const room = roomName(part);
  return (
    <div className={styles.partFocus} aria-label={`Del ${part.code}`} role="group">
      <h3 className={styles.sectionHead}>
        <span>
          Del {part.code}
          {room ? ` · ${room}` : ''}
        </span>
        <span className={styles.sectionTools}>
          {!isScanPart(part) && <Pill variant="outline">Manuel</Pill>}
          {part.orphaned && (
            <Pill tone="warn" title="Instansen findes ikke længere — gennemgå eller flyt delen">
              forældet
            </Pill>
          )}
          <TextLink size="sm" nowrap onClick={onClose} aria-label={`Luk del ${part.code}`}>
            Luk
          </TextLink>
        </span>
      </h3>
      <PartFields
        type={type}
        part={part}
        resource={resource}
        catalogue={catalogue}
        canEdit={canEdit}
        home={home}
        onPatch={onPatch}
        onCellCommit={onCellCommit}
        onInvalid={onInvalid}
        layout="inline"
      />
      {canEdit && (
        <div className={styles.secondary}>
          <Button size="sm" disabled={busy} onClick={() => onPatch({ starred: !part.starred })}>
            {part.starred ? '★ Fjern vigtig' : '☆ Markér vigtig'}
          </Button>
          <Button
            size="sm"
            tone="danger"
            armed={armed}
            disabled={busy}
            onBlur={confirm.disarm}
            onClick={(e) => {
              if (!armed) {
                confirm.arm(part.code, e.currentTarget);
                return;
              }
              confirm.disarm();
              onDelete();
            }}
          >
            {armed ? `Bekræft: slet ${part.code}` : isScanPart(part) ? 'Markér som fejl' : 'Slet del'}
          </Button>
        </div>
      )}
    </div>
  );
}
